import { useEffect, useMemo, useState } from "react";
import { getInstitutions } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import PageHero from "../components/layout/PageHero";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import { InstitutionCard } from "../components/Cards";
import { CardSkeletons, ErrorState, EmptyState } from "./Home";
import { Reveal } from "../components/motion/Reveal";

const selectCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 transition-colors duration-200 focus:border-navy focus:outline-none";

export default function Institutes() {
  useSEO(
    "Institutes — Suvera Academy",
    "Featured partner universities, colleges and pathway providers across the UK, Mauritius, Singapore and beyond."
  );
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [area, setArea] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    getInstitutions()
      .then(setInstitutions)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => [...new Set(institutions.map((i) => i.country))].sort(), [institutions]);
  const areas = useMemo(() => [...new Set(institutions.flatMap((i) => i.programs))].sort(), [institutions]);
  const types = useMemo(() => [...new Set(institutions.map((i) => i.type))].sort(), [institutions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return institutions.filter((i) => {
      if (q && !`${i.name} ${i.city} ${i.country} ${i.programs.join(" ")}`.toLowerCase().includes(q)) return false;
      if (country && i.country !== country) return false;
      if (type && i.type !== type) return false;
      if (area && !i.programs.includes(area)) return false;
      return true;
    });
  }, [institutions, query, country, area, type]);

  return (
    <main data-testid="institutes-page">
      <PageHero
        eyebrow="Featured Institutes"
        title="Connecting You to Universities That Inspire Success"
        description="At Suvera Academy we believe the right education opens the door to endless opportunities. Our featured partners span the UK, Mauritius, Singapore and beyond — globally recognized institutions offering world-class programs, innovative teaching, and career-driven outcomes."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Institutes" }]}
        testid="institutes-hero"
      />

      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
        <div className="mb-10 grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="institute-filters">
          <label className="relative block">
            <Icon name="Search" className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className={`${selectCls} pl-10`}
              placeholder="Search institutions, cities, programs..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="institute-search-input"
              aria-label="Search institutions"
            />
          </label>
          <select className={selectCls} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="institute-country-filter" aria-label="Filter by country">
            <option value="">All Countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className={selectCls} value={area} onChange={(e) => setArea(e.target.value)} data-testid="institute-area-filter" aria-label="Filter by study area">
            <option value="">All Study Areas</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className={selectCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="institute-type-filter" aria-label="Filter by institution type">
            <option value="">All Types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {loading ? (
          <CardSkeletons count={6} />
        ) : error ? (
          <ErrorState message="Institutions could not be loaded. Please refresh the page." />
        ) : filtered.length === 0 ? (
          <EmptyState message="No institutions found for this filter. Try adjusting your search." />
        ) : (
          <>
            <p className="mb-6 font-mono-accent text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="institute-results-count">
              {filtered.length} institution{filtered.length === 1 ? "" : "s"} found
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="institutes-grid">
              {filtered.map((i, idx) => (
                <Reveal key={i.slug} delay={(idx % 3) * 0.06} className="h-full"><InstitutionCard i={i} /></Reveal>
              ))}
            </div>
          </>
        )}
      </section>
      <CTASection title="Partnering With the Best to Build Your Future" description="Not sure which institution fits your goals? Speak with a Suvera Academy advisor for a personalised shortlist." />
    </main>
  );
}
